import { Component, Input, Output, EventEmitter, forwardRef, OnInit } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { CommonModule } from '@angular/common';
import Quill from 'quill';
import QuillTableBetter from 'quill-table-better';

// Register the table-better module
Quill.register('modules/table-better', QuillTableBetter);

// Register custom fonts
const Font = Quill.import('formats/font') as any;
Font.whitelist = [
  'segoe',
  'sf',
  'helvetica',
  'arial',
  'calibri'
];
Quill.register(Font, true);

/**
 * Rich text editor component based on ngx-quill.
 *
 * Features:
 * - Full table support (table, tr, td, th, tbody, thead tags and attributes like colspan, rowspan)
 * - ControlValueAccessor implementation for form integration
 * - Configurable toolbar and modules
 * - PrimeNG theme integration
 * - HTML sanitization disabled to preserve all tags and attributes
 *
 * Usage:
 * ```html
 * <app-rich-text-editor [(ngModel)]="content"></app-rich-text-editor>
 * ```
 */
@Component({
  selector: 'app-rich-text-editor',
  standalone: true,
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  imports: [QuillModule, FormsModule, CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
})
export class RichTextEditorComponent implements ControlValueAccessor, OnInit {
  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() class: string = '';
  @Input() invalid: boolean = false;

  @Output() contentChanged = new EventEmitter<any>();
  @Output() editorCreated = new EventEmitter<any>();

  // Default modules configuration with full table support
  defaultModules = {
    toolbar: {
      container: [
        [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
        [{ font: ['segoe', 'sf', 'helvetica', 'arial', 'calibri'] }],
        ['bold', 'italic', 'underline', 'strike'], // toggled buttons
        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
        ['link', 'image'],
        ['table-better'], // Add table tool
      ],
    },
    table: false,
    'table-better': {
      language: 'en_US',
      menus: [
        'column',
        'row',
        'merge',
        'table',
        'cell',
        'wrap',
        'copy',
        'delete',
      ],
      toolbarTable: true,
    },
    keyboard: {
      bindings: QuillTableBetter.keyboardBindings,
    },
  };

  content: string = '';
  private lastEmittedValue: string | null = null;
  private quillInstance: any = null;

  get componentClass(): string {
    let baseClass = this.class || '';
    if (this.invalid) {
      baseClass += ' p-invalid';
    }
    return baseClass.trim();
  }

  ngOnInit() {
  }

  // ControlValueAccessor implementation
  private onChangeFn: (value: any) => void = () => { };
  private onTouchedFn: () => void = () => { };

  writeValue(value: any): void {
    const newValue = value || '';

    // Normalize tables before setting content
    //const normalizedValue = newValue ? this.normalizeTablesOnly(newValue) : '';
    const normalizedValue = newValue;

    this.content = normalizedValue;
    // Record the value being written to prevent unnecessary updates
    this.lastEmittedValue = normalizedValue;

    // If quill instance exists, use updateContents
    if (this.quillInstance && normalizedValue) {
      this.setContentUsingUpdateContents(normalizedValue);
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onContentChanged(event: any): void {
    const newContent = event.html || event.text || '';

    // Only respond to user input to prevent loops from Quill's HTML normalization
    if (event.source === 'user') {
      this.content = newContent;
      this.lastEmittedValue = newContent;
      this.onChangeFn(this.content);
      this.contentChanged.emit(event);
    }
  }

  onEditorCreated(event: any): void {
    this.quillInstance = event;
    this.editorCreated.emit(event);

    // If there's initial content, set it using updateContents
    if (this.content) {
      this.setContentUsingUpdateContents(this.content);
    }
  }

  onBlur(): void {
    this.onTouchedFn();
  }

  private setContentUsingUpdateContents(html: string): void {
    if (!this.quillInstance) return;

    const delta = this.quillInstance.clipboard.convert({ html });
    const range = this.quillInstance.getSelection();

    this.quillInstance.updateContents(delta, Quill.sources.USER);
    this.quillInstance.setSelection(
      delta.length() - (range?.length || 0),
      Quill.sources.SILENT
    );
    this.quillInstance.scrollSelectionIntoView();
  }

  /**
 * Normalize ONLY <table> elements so they can be safely consumed by QuillTableBetter.
 *
 * Design goals:
 * - Touch <table> elements and their descendants ONLY
 * - Preserve all non-table HTML exactly as-is
 * - Convert "browser-valid" tables into "QuillTableBetter-valid" tables
 *
 * What this function guarantees:
 * - <tbody> always exists
 * - <tbody> contains only <tr>
 * - <tr> contains only <td> / <th>
 * - Removes unsupported table structures (thead, tfoot, colgroup)
 * - Keeps structural attributes (colspan / rowspan)
 * - Drops styling and complex nested markup inside cells
 *
 * What this function intentionally does NOT do:
 * - Sanitize or modify non-table HTML
 * - Preserve complex table styling
 * - Support nested tables or Word/Outlook-specific markup
 */
  private normalizeTablesOnly(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Process each table independently
    doc.querySelectorAll('table').forEach(table => {

      // 1️⃣ Remove unsupported table sections
      // QuillTableBetter expects a very strict table structure.
      table.querySelectorAll('colgroup, thead, tfoot').forEach(node => {
        node.remove();
      });

      // 2️⃣ Ensure <tbody> exists
      // If missing, move all direct <tr> children into a new <tbody>
      let tbody = table.querySelector('tbody');
      if (!tbody) {
        tbody = doc.createElement('tbody');

        Array.from(table.children).forEach(child => {
          if (child.tagName === 'TR') {
            tbody!.appendChild(child);
          }
        });

        table.appendChild(tbody);
      }

      // 3️⃣ Normalize rows and cells
      Array.from(tbody.children).forEach(row => {
        // Remove anything inside <tbody> that is not a <tr>
        if (row.tagName !== 'TR') {
          row.remove();
          return;
        }

        Array.from(row.children).forEach(cell => {
          // Cells must be either <td> or <th>
          if (cell.tagName !== 'TD' && cell.tagName !== 'TH') {
            cell.remove();
            return;
          }

          // Flatten cell content to plain text only
          // This prevents QuillTableBetter from rejecting complex markup
          const textContent = cell.textContent ?? '';
          cell.innerHTML = textContent;
        });
      });

      // 4️⃣ Clean table-level attributes
      // Retain only attributes commonly used for email compatibility
      Array.from(table.attributes).forEach(attr => {
        const allowed = ['border', 'cellpadding', 'cellspacing'];
        if (!allowed.includes(attr.name)) {
          table.removeAttribute(attr.name);
        }
      });
    });

    // Return full HTML; only tables were modified
    return doc.body.innerHTML;
  }
}
